import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import get from 'lodash/get';

import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import { downloadService, uploadPortfolio, postOrderMany } from 'services';

import { portfolioUploadSteps as steps } from 'shared/constants/portfolioUploadSteps';

import { portfolioUploadSchema, createPortfolioSchema } from 'shared/validations';
import { purchaseTypes } from 'shared/constants/purchaseTypes';
import { orderTypes } from 'shared/constants/orderTypes';
import { LinkButton } from 'ui/Buttons';
import { useNotifications } from 'shared/hooks';
import { checkDossierName, addToDossierIsin } from 'services/dossier/dossierService';
import { refreshDossiersAction } from 'store/portfolio/dossierActions';
import { Loader } from 'ui';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { formatNumber } from 'shared/functions';
import { green } from '@mui/material/colors';

const useStyles = makeStyles(theme => ({
  downloadTemplateContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  buttonIcon: {
    marginRight: theme.spacing(1),
  },
  downloadButtonContainer: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  input: {
    display: 'none',
  },
  uploadContainer: {
    padding: '2px 4px',
    margin: theme.spacing(2, 0),
    display: 'flex',
    alignItems: 'center',
  },
  fileUploadText: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  fullWidth: {
    width: '100%',
  },
  summaryContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  textCenter: {
    textAlign: 'center',
  },
  doneIcon: {
    color: green[600],
    width: 140,
    height: 140,
  },
  selectButton: {
    width: 150,
    height: 100,
    textTransform: 'inherit',
    border: 'none',
    color: theme.palette.text.primary,
    boxShadow: '0 4px 4px 2px rgba(155, 155, 155, 0.3)',
    padding: '0 35px',
  },
  selectTitle: {
    fontSize: 15,
  },
}));

const PortfolioUploadCreditRating = ({ onClose, backToTypeSelection }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { notifyError } = useNotifications();

  const [fileUploadInputRef, setFileUploadInputRef] = useState(null);

  const [dossierName, setDossierName] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(steps.UPLOAD);
  const [summaryStep, setSummaryStep] = useState(0);

  useEffect(() => {
    if (error) {
      notifyError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const summarySteps = [
    {
      label: t('Step one'),
      text: t(
        'Have ratings that are immediately available for you and will be accessible for you free of charge',
      ),
      ratingsCount: get(data, 'publicRatings.length', 0),
    },
    {
      label: t('Step two'),
      text: t(
        'Have ratings that are currently in process and will be accessible for you free of charge. You will be notified as soon as the ratings are available.',
      ),
      ratingsCount: get(data, 'subscriptionRatingsFree.length', 0),
    },
    {
      label: t('Step three'),
      text: t(
        'Have ratings analysis that are subject to a charge and are accessible after payment.',
      ),
      ratingsCount: get(data, 'subscriptionRatingsChargeable.length', 0),
    },
  ];

  const sampleTemplateUrl = '/storefront/document/sample/ISIN';

  const fileUploadInputRefChanged = element => setFileUploadInputRef(element);

  const artifactIdsToProducts = (artifactIds, isPublic) =>
    artifactIds.map(artifactId => ({
      artifactId,
      artifactType: orderTypes.INSTRUMENT,
      purchaseType: purchaseTypes.ONE_TIME,
      frequency: 0,
      productCategory: isPublic ? 'L5' : 'L4',
    }));

  const placeOrders = async () => {
    setIsPending(true);

    const subscriptionProducts = artifactIdsToProducts(
      [...data.subscriptionRatingsFree, ...data.subscriptionRatingsChargeable],
      false,
    );
    const publicProducts = artifactIdsToProducts(data.publicRatings, true);
    const products = [...subscriptionProducts, ...publicProducts];
    const dossierItems = products.map(({ artifactId, artifactType }) => ({
      artifactId,
      type: artifactType,
    }));

    try {
      const orderResponse = await postOrderMany({ products, isin: true });
      const addToDossierResponse = await addToDossierIsin({
        items: dossierItems,
        dossierName,
        artifacts: dossierItems,
      });

      if (orderResponse.error) {
        throw orderResponse;
      } else if (addToDossierResponse.error) {
        throw addToDossierResponse;
      }

      dispatch(refreshDossiersAction({ reason: null }));
      setStep(steps.FINISH);
    } catch (e) {
      setError('Failed to finish portfolio upload');
    }
    setIsPending(false);
  };

  const handleStepSubmit = async payload => {
    if (step === steps.UPLOAD) {
      if (step === steps.UPLOAD) {
        setIsPending(true);
        try {
          const { error: uploadError, data: uploadData } = await uploadPortfolio({
            portfolio: payload && payload.fileUploadInput,
          });
          if (uploadError) {
            throw new Error(uploadError);
          } else {
            setData({ ...uploadData, fileName: payload.fileUploadInput.name });
            setStep(steps.CREATE_DOSSIER);
          }
        } catch (e) {
          setError('Something went wrong. Make sure the file is not corrupted.');
        }
        setIsPending(false);
      }
    } else if (step === steps.CREATE_DOSSIER) {
      setIsPending(true);
      try {
        const response = await checkDossierName({
          dossierName: payload && payload.dossierName,
          ratingType: ratingTypes.CREDIT_RATING,
        });
        if (response.error) {
          throw response;
        } else if (response.dossierNameExists) {
          throw new Error('Dossier name already exists');
        }
        setDossierName(payload.dossierName);
        setStep(steps.SUMMARY);
      } catch (e) {
        setError(e.message);
        setDossierName(null);
      }
      setIsPending(false);
    }
  };

  const downloadErrorHandler = () => {
    notifyError(t('Failed to download sample portfolio template'));
  };

  const handleDownloadTemplateClick = () =>
    downloadService({
      path: sampleTemplateUrl,
      name: 'portfolio-template.csv',
      onError: downloadErrorHandler,
    });

  const nextSummaryStep = () => {
    if (summaryStep < summarySteps.length - 1) {
      setSummaryStep(summaryStep + 1);
    } else {
      placeOrders();
    }
  };

  const updateUploadBoxData = (setFieldValueFunc, file, text) => {
    if (!file && fileUploadInputRef) {
      fileUploadInputRef.value = null;
    }
    setFieldValueFunc('fileUploadInput', file);
    setFieldValueFunc('fileUploadText', text);
  };

  const closeModal = () => {
    setSummaryStep(0);
    onClose();
  };

  const stepUploadInitialValues = {
    description: '',
    origin: window.location.href,
    fileUploadText: '',
    fileUploadInput: null,
  };

  const stepCreateDossierInitialValues = {
    dossierName: '',
  };

  const stepUpload = (
    <Formik
      key={steps.UPLOAD}
      initialValues={stepUploadInitialValues}
      onSubmit={payload => handleStepSubmit(payload)}
      validationSchema={portfolioUploadSchema(['csv'])}
    >
      {({ values, errors, touched, setFieldValue }) => {
        return (
          <Form>
            <DialogContent>
              <Box mb={2}>
                <Typography>
                  {t(
                    `The upload of your portfolio is free of charge and does not trigger any costs.
               Please select a file from your computer.`,
                  )}
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography>
                  {t(
                    `For the portfolio upload you require an ISIN overview in excel (.csv). 
               The import  of an ISIN file automatically triggers a request for rating 
               for the relevant items.`,
                  )}
                </Typography>
              </Box>
              <Divider />
              <div className={classes.downloadTemplateContainer}>
                <Box p={2}>
                  <Typography>{`${t('File format')}: .csv`}</Typography>
                  <Typography>{`${t('Max size')}: 5 MB`}</Typography>
                  <Typography>{`${t('Column heading')}: ISIN`}</Typography>
                </Box>
                <Box p={2} className={classes.downloadButtonContainer}>
                  <Button
                    onClick={handleDownloadTemplateClick}
                    variant="outlined"
                    disabled={isPending}
                  >
                    {t('DOWNLOAD_TEMPLATE')}
                  </Button>
                </Box>
              </div>
              <Divider />
              <ErrorMessage name="fileUploadInput">
                {message => (
                  <Box mt={2}>
                    <Typography color="error">{message}</Typography>
                  </Box>
                )}
              </ErrorMessage>
              <Paper className={classes.uploadContainer}>
                <input
                  className={classes.input}
                  id="file-upload"
                  name="file-upload"
                  ref={fileUploadInputRefChanged}
                  type="file"
                  onChange={event => {
                    updateUploadBoxData(
                      setFieldValue,
                      event.currentTarget.files[0],
                      get(event.currentTarget.files, '[0].name', ''),
                    );
                  }}
                />
                <Field name="fileUploadText">
                  {({ field }) => (
                    <InputBase
                      {...field}
                      className={classes.fileUploadText}
                      placeholder={t('UPLOAD.NO_FILE_SELECTED')}
                      error={errors.fileUploadInput && touched.fileUploadText}
                      readOnly
                    />
                  )}
                </Field>
                <IconButton
                  type="submit"
                  className={classes.iconButton}
                  disabled={!fileUploadInputRef || !fileUploadInputRef.value}
                  onClick={() => {
                    updateUploadBoxData(setFieldValue, null, '');
                  }}
                  size="large">
                  <DeleteIcon />
                </IconButton>
                <label htmlFor="file-upload">
                  <Button variant="contained" color="primary" component="span">
                    {t('UPLOAD.BROWSE')}
                  </Button>
                </label>
              </Paper>
            </DialogContent>
            <DialogActions>
              {step !== steps.FINISH && (
                <Box>
                  <Button variant="outlined" onClick={backToTypeSelection}>
                    {t('Previous')}
                  </Button>
                </Box>
              )}
              <div style={{ flex: '1 0 0' }} />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isPending || !get(values, 'fileUploadInput')}
              >
                {t('Next')}
              </Button>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );

  const stepCreateDossier = (
    <Formik
      key={steps.CREATE_DOSSIER}
      className={classes.fullWidth}
      initialValues={stepCreateDossierInitialValues}
      onSubmit={payload => handleStepSubmit(payload)}
      validationSchema={createPortfolioSchema}
    >
      {({ errors, touched }) => {
        return (
          <Form>
            <DialogContent>
              <Box mb={2}>
                <Typography>{t('The file selected below will be uploaded.')}</Typography>
              </Box>
              <div className={classes.downloadTemplateContainer}>
                <Box p={2}>
                  <Typography color="primary">{`${data.fileName}`}</Typography>
                </Box>
                <Box p={2} className={classes.downloadButtonContainer}>
                  <Button
                    onClick={() => setStep(steps.UPLOAD)}
                    variant="contained"
                    color="primary"
                    disabled={isPending}
                  >
                    {t('Change File')}
                  </Button>
                </Box>
              </div>
              <Box mt={2}>
                <Typography>{t('Create a portfolio to add the above file')}</Typography>
              </Box>
              <Field name="dossierName">
                {({ field }) => (
                  <TextField
                    {...field}
                    type="text"
                    label="Portfolio name"
                    error={errors.dossierName && touched.dossierName}
                    fullWidth
                  />
                )}
              </Field>
            </DialogContent>
            <DialogActions>
              {step !== steps.FINISH && (
                <Box>
                  <Button variant="outlined" onClick={backToTypeSelection}>
                    {t('Previous')}
                  </Button>
                </Box>
              )}
              <div style={{ flex: '1 0 0' }} />
              <Button type="submit" variant="contained" color="primary" disabled={isPending}>
                {t('Next')}
              </Button>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );

  const stepSummary = (
    <>
      <DialogContent>
        <Box mb={2}>
          <Stepper activeStep={summaryStep}>
            {summarySteps.map(item => {
              return (
                <Step key={item.label}>
                  <StepLabel>{item.label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>
        <Box mb={2} className={classes.summaryContent}>
          <Typography color="primary" variant="h3">
            {summarySteps[summaryStep].ratingsCount}
          </Typography>
          <Box p={2}>
            <Typography variant="h6">{t('out of')}</Typography>
          </Box>
          <Typography variant="h3">{data ? formatNumber(data.total) : 0}</Typography>
        </Box>
        <Box p={2} mb={2}>
          <Typography variant="body1" className={classes.textCenter}>
            {summarySteps[summaryStep].text}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box pl={2}>
          <Typography variant="caption">
            {t('By requesting an instrument you will also request the respective entity.')}
          </Typography>
        </Box>
        <div style={{ flex: '1 0 0' }} />
        <Button variant="contained" color="primary" disabled={isPending} onClick={nextSummaryStep}>
          {t('Next')}
        </Button>
      </DialogActions>
    </>
  );

  const stepFinish = (
    <>
      <DialogContent className={classes.textCenter}>
        <DoneIcon className={classes.doneIcon} />
        <Box p={2}>
          <Typography variant="h6">
            {t('Your personal advisor will contact you to discuss your request')}
          </Typography>
        </Box>
        <Box p={2} mb={2}>
          <Typography variant="body1">
            {t(
              'If a requested entity mandates the rating in the interim, the analysis will be provided free of charge.',
            )}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <LinkButton
          to="/portfolios"
          label={t('View portfolios')}
          variant="contained"
          color="primary"
          disabled={isPending}
          onClick={closeModal}
        />
      </DialogActions>
    </>
  );

  const stepsMapping = {
    [steps.UPLOAD]: stepUpload,
    [steps.CREATE_DOSSIER]: stepCreateDossier,
    [steps.SUMMARY]: stepSummary,
    [steps.FINISH]: stepFinish,
  };

  return (
    <>
      {stepsMapping[step]}
      {isPending && <Loader cover />}
    </>
  );
};

PortfolioUploadCreditRating.defaultProps = {
  onClose: () => null,
  onPortfolioCreate: () => null,
  dossierItem: null,
  items: [],
  backToTypeSelection: () => null,
  onClickCreateNew: () => null,
  createdPortfolioName: undefined,
};

PortfolioUploadCreditRating.propTypes = {
  onClose: PropTypes.func,
  onPortfolioCreate: PropTypes.func,
  dossierItem: PropTypes.object,
  items: PropTypes.arrayOf(PropTypes.object),
  backToTypeSelection: PropTypes.func,
  onClickCreateNew: PropTypes.func,
  createdPortfolioName: PropTypes.string,
};

export default PortfolioUploadCreditRating;
